import "react-toastify/dist/ReactToastify.css";

import dayjs from "dayjs";
import "dayjs/locale/uk";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
dayjs.locale("uk");

import {
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration, useLoaderData,
    isRouteErrorResponse,
    useRouteError,
} from "react-router";

import ErrorPage from "@/components/errors/ErrorPage";

import {AuthProvider} from "@/providers/AuthProvider";
import {ApiProvider} from "@/providers/ApiProvider";
import {NotificationsProvider} from "@/providers/NotificationsProvider";
import {GoogleMapsApiProvider} from "@/providers/googleMapsApiProvider";
import {MapProvider} from "@/providers/MapProvider";

import {ClientOnly} from "@/components/common/ClientOnly";
import {LeafletDefaults} from "@/components/map/LeafletDefaults";
import {ToastContainer} from "react-toastify";

import {getUserWithAutoRefresh} from "@/auth/session.server";

export async function loader({request}) {
    const {user, setCookies} = await getUserWithAutoRefresh(request);
    const headers = new Headers();
    setCookies.forEach((c) => headers.append("Set-Cookie", c));

    return Response.json({user}, {headers});
}

export function Layout({children}) {
    return (
        <html lang="uk">
        <head>
            <meta charSet="utf-8"/>
            <meta name="viewport" content="width=device-width,initial-scale=1"/>

            <link rel="icon" type="image/png" href="/favicon/favicon-96x96.png" sizes="96x96" />
            <link rel="icon" type="image/svg+xml" href="/favicon/favicon.svg" />
            <link rel="shortcut icon" href="/favicon/favicon.ico" />
            <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon-v2.png" />
            <link rel="manifest" href="/site.webmanifest" />
            <meta name="apple-mobile-web-app-title" content="Likengale" />
            <meta name="application-name" content="Likengale" />

            <Meta/>
            <Links/>
        </head>
        <body>
        {children}
        <ScrollRestoration/>
        <Scripts/>
        </body>
        </html>
    );
}

export function ErrorBoundary() {
    const error = useRouteError();

    let title = "Щось пішло не так";
    let message = "Сталася непередбачена помилка. Спробуйте перезавантажити сторінку або повернутися на головну.";
    let details = null;

    if (isRouteErrorResponse(error)) {
        if (error.status === 404) {
            title = "Сторінку не знайдено";
            message = "Сторінка, яку ви шукаєте, не існує або була переміщена.";
        } else {
            title = `Помилка ${error.status}`;
            message = error.statusText || message;
            if (error.data) {
                details = typeof error.data === "string" ? error.data : JSON.stringify(error.data);
            }
        }
    } else if (error instanceof Error) {
        details = error.message;
    }

    return (
        <ErrorPage
            title={title}
            message={message}
            details={details}
            showReload={!isRouteErrorResponse(error) || error.status !== 404}
        />
    );
}

export default function App() {
    const {user} = useLoaderData();

    return (
        <AuthProvider initialUser={user}>
            <GoogleMapsApiProvider>
                <MapProvider>
                    <ClientOnly>
                        <LeafletDefaults/>
                    </ClientOnly>

                    <ApiProvider>
                        <NotificationsProvider>
                            <Outlet/>
                            <ToastContainer />
                        </NotificationsProvider>
                    </ApiProvider>
                </MapProvider>
            </GoogleMapsApiProvider>
        </AuthProvider>
    );
}
